import { ExternalLinkIcon, MailOpenIcon } from '@heroicons/react/outline';
import { CheckIcon, MailIcon, PaperClipIcon, StarIcon as SolidStarIcon } from '@heroicons/react/solid';
import * as React from 'react';
import * as NotificationApi from '~/Api/Pigeon';
import { groupBy } from '~/Collections';
import { formatISODate, formatNumericUserDate, formatTime, formatUserDateWithWeekday } from '~/Date';
import { useToggle } from '~/Hooks';
import { Tooltip } from '~/UI';
import { Card } from '~/UI/Card';
export var Feed = function (_a) {
    var EmailBodyLink = _a.EmailBodyLink, notifications = _a.notifications, status = _a.status, onSetPriority = _a.onSetPriority, onSetRead = _a.onSetRead, onSetReadAll = _a.onSetReadAll;
    return (<div className='container mx-auto'>
		<div className='mt-10 px-4'>
			<div className='px-4 mb-10'>
				{notifications.length === 0 ? <p className='p-4 text-base'>Nemáte žádné notifikace</p> : null}
				{Array.from(groupByDays(notifications).entries()).map(function (value) {
            var items = value[1];
            return (<Day day={items[0].createdTime} EmailBodyLink={EmailBodyLink} key={formatISODate(items[0].createdTime)} notifications={items} status={status} onSetPriority={onSetPriority} onSetRead={onSetRead} onSetReadAll={onSetReadAll}/>);
        })}
			</div>
		</div>
	</div>);
};
Feed.displayName = 'NotificationList.Feed';
export var Day = function (_a) {
    var day = _a.day, EmailBodyLink = _a.EmailBodyLink, notifications = _a.notifications, onSetPriority = _a.onSetPriority, onSetRead = _a.onSetRead, onSetReadAll = _a.onSetReadAll;
    return (<div className='mb-10' key={formatISODate(day)}>
		<Card>
			<Header day={day} onSetReadAll={function () {
            onSetReadAll(notifications.map(function (notification) { return notification.id; }));
        }}/>
			<List EmailBodyLink={EmailBodyLink} notifications={notifications} onSetPriority={onSetPriority} onSetRead={onSetRead}/>
		</Card>
	</div>);
};
var Header = function (_a) {
    var day = _a.day, onSetReadAll = _a.onSetReadAll;
    return (<div className='flex flex-row justify-between flex-wrap w-full h-16 py-4 pl-5 pr-2 sticky top-0 bg-white border-b border-cogvio-100'>
		<h2 className='text-gray-500 text-xl'>{formatUserDateWithWeekday(day, ['cs'])}</h2>
		<div className='flex flex-row items-center gap-5'>
			<Tooltip title='Označit všechny zprávy v daný den jako přečtené' width='w-48'>
				<CheckIcon aria-hidden='true' className='p-1 flex-shrink-0 inline h-7 w-7 text-gray-500 cursor-pointer stroke-2 rounded-full hover:bg-green-700 hover:text-white' onClick={onSetReadAll}/>
			</Tooltip>
		</div>
	</div>);
};
Header.displayName = 'NotificationList.Header';
var List = function (_a) {
    var EmailBodyLink = _a.EmailBodyLink, notifications = _a.notifications, onSetPriority = _a.onSetPriority, onSetRead = _a.onSetRead;
    return (<div className='divide-y divide-gray-200 rounded-md'>
		{notifications.map(function (notification) { return (<Row EmailBodyLink={EmailBodyLink} key={notification.id} notification={notification} onSetPriority={onSetPriority} onSetRead={onSetRead}/>); })}
	</div>);
};
List.displayName = 'NotificationList.List';
var Row = function (_a) {
    var _b, _c, _d, _e;
    var EmailBodyLink = _a.EmailBodyLink, notification = _a.notification, onSetPriority = _a.onSetPriority, onSetRead = _a.onSetRead;
    var _f = useToggle(), expanded = _f[0], toggleExpanded = _f[1];
    var email = notification.email;
    return (<div className='flex flex-col group'>
			<div className={"flex flex-row group-hover:bg-gray-50"}>
				<div className='py-2 pl-4 flex-shrink-0'>
					{notification.authenticatedRecipient.isRead ? (<MailOpenIcon aria-hidden='true' className='flex-shrink-0 inline h-9 w-9 p-2 text-cogvio-300'/>) : (<MailIcon aria-hidden='true' className='flex-shrink-0 inline h-9 w-9 p-2 text-cogvio-300' onClick={function () {
                onSetRead(notification.id);
            }}/>)}
					{notification.authenticatedRecipient.priority === NotificationApi.NotificationPriority.High ? (<SolidStarIcon aria-hidden='true' className='flex-shrink-0 inline h-9 w-9 p-2 mr-1 text-yellow-400 rounded-full hover:bg-yellow-200 cursor-pointer' onClick={function () {
                onSetPriority(notification.id, false);
            }}/>) : (<SolidStarIcon aria-hidden='true' className='flex-shrink-0 inline h-9 w-9 p-2 mr-1 text-gray-200 rounded-full hover:bg-yellow-200 cursor-pointer' onClick={function () {
                onSetPriority(notification.id, true);
            }}/>)}
				</div>
				<a className='flex flex-row flex-grow justify-between py-4 pr-4 text-sm cursor-pointer md:overflow-hidden' onClick={function () {
            toggleExpanded();
            if (!notification.authenticatedRecipient.isRead) {
                onSetRead(notification.id);
            }
        }}>
					<div className='md:truncate pr-2'>
						<span className={"".concat(notification.authenticatedRecipient.isRead ? '' : 'font-semibold')}>
							{(_c = (_b = notification.email) === null || _b === void 0 ? void 0 : _b.subject) !== null && _c !== void 0 ? _c : 'Notifikace'}
						</span>
						{!expanded ? (<span className='text-gray-600 pl-2 text-xs'>
								{notification.perex !== null ? "".concat(notification.perex.substr(0, 500), "\u2026") : null}
							</span>) : null}
					</div>
					<span className='block text-gray-500'>
						{((_e = (_d = notification.email) === null || _d === void 0 ? void 0 : _d.attachments.length) !== null && _e !== void 0 ? _e : 0) > 0 ? (<PaperClipIcon aria-hidden='true' className='inline flex-shrink-0 h-4 w-4 mr-2 text-gray-400'/>) : null}
						{formatTime(notification.createdTime, ['cs'])}
					</span>
				</a>
			</div>
			{expanded ? (<div className={"flex flex-row pl-7 pb-3 group-hover:bg-gray-50"}>
					{email !== null ? (<div className='w-full'>
							<dl>
								<DetailRow title='Odesílatel'>
									<InternetAddressHandle address={email.from}/>
								</DetailRow>
								<DetailRow title='Příjemce'>
									{email.to.map(function (recipient, id) { return (<InternetAddressHandle address={recipient} key={id}/>); })}
								</DetailRow>
								{email.cc.length > 0 ? (<DetailRow title='Cc'>
										{email.cc.map(function (recipient, id) { return (<InternetAddressHandle address={recipient} key={id}/>); })}
									</DetailRow>) : null}
								{notification.perex !== null ? (<DetailRow title='Obsah'>
										<div className='text-xs pb-4'>{"".concat(notification.perex.substr(0, 500), "\u2026")}</div>
									</DetailRow>) : null}
							</dl>
							<div className='flex flex-row-reverse w-full px-4'>
								<EmailBodyLink emailId={email.id} notificationId={notification.id}>
									<a className='mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 bg-cogvio-900 text-white text-base font-medium
										transition duration-200 ease-in-out hover:bg-cogvioBlue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'>
										Zobrazit obsah
										<ExternalLinkIcon className='ml-2 h-5 w-5 text-gray-200'/>
									</a>
								</EmailBodyLink>
							</div>
						</div>) : null}
				</div>) : null}
		</div>);
};
Row.displayName = 'NotificationList.Row';
export var DetailRow = function (_a) {
    var children = _a.children, title = _a.title;
    return (<div className='px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
		<dt className='text-sm font-medium text-gray-500'>{title}</dt>
		<dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>{children}</dd>
	</div>);
};
DetailRow.displayName = 'Application.Notification.DetailRow';
export var InternetAddressHandle = function (_a) {
    var address = _a.address;
    return (<div>{address.personal !== null ? "".concat(address.personal, " <").concat(address.emailAddress, ">") : "".concat(address.emailAddress)}</div>);
};
InternetAddressHandle.displayName = 'Application.Notification.InternetAddressHandle';
var groupByDays = function (notifications) {
    return groupBy(notifications, function (notification) { return formatNumericUserDate(notification.createdTime, ['en']); });
};
